import React, { useState } from 'react';
import { useApp, PageView } from '../../context/AppContext';
import {
  LayoutDashboard,
  WalletCards,
  FileText,
  CheckSquare,
  BarChart3,
  CalendarDays,
  Flame,
  Settings,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Target,
  Bookmark,
  LogOut,
} from 'lucide-react';
import { QuickAddModal } from '../common/QuickAddModal';

export const Sidebar: React.FC = () => {
  const { currentPage, setCurrentPage, logout, language, t } = useApp();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);

  const isBn = language === 'bn';

  const navItems: { page: PageView; label: string; icon: any }[] = [
    { page: 'dashboard', label: t.dashboard, icon: LayoutDashboard },
    { page: 'finance', label: t.finance, icon: WalletCards },
    { page: 'tasks', label: t.tasks, icon: CheckSquare },
    { page: 'notes', label: t.notes, icon: FileText },
    { page: 'goals', label: t.goals || 'Year Goals', icon: Target },
    { page: 'resources', label: t.resources || 'Resources', icon: Bookmark },
    { page: 'habits', label: t.habits, icon: Flame },
    { page: 'calendar', label: t.calendar, icon: CalendarDays },
    { page: 'reports', label: t.reports, icon: BarChart3 },
    { page: 'settings', label: t.settings, icon: Settings },
  ];

  const handleLogout = () => {
    if (window.confirm(isBn ? 'আপনি কি নিশ্চিত যে আপনি লগআউট করতে চান?' : 'Are you sure you want to sign out?')) {
      logout();
    }
  };

  return (
    <>
      <aside
        className={`hidden lg:flex flex-col sticky top-16 h-[calc(100vh-4rem)] overflow-hidden flex-shrink-0 border-r border-slate-200/80 dark:border-slate-800/80 bg-white/50 dark:bg-[#0b0f19]/50 backdrop-blur-md transition-all duration-300 select-none print:hidden ${
          isCollapsed ? 'w-16 p-2' : 'w-48 p-2.5'
        }`}
      >
        {/* Quick Action Button */}
        <button
          onClick={() => setIsQuickAddOpen(true)}
          className={`w-full mb-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-500/20 flex items-center justify-center gap-1.5 transition-all transform active:scale-95 ${
            isCollapsed ? 'px-0' : 'px-2'
          }`}
          title={t.quickAdd}
        >
          <PlusCircle className="w-4 h-4 flex-shrink-0" />
          {!isCollapsed && <span>{t.quickAdd}</span>}
        </button>

        {/* Main Navigation Links */}
        <nav className="flex-1 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.page;

            return (
              <button
                key={item.page}
                onClick={() => setCurrentPage(item.page)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                  isActive
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-extrabold shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                } ${isCollapsed ? 'justify-center px-0' : ''}`}
                title={item.label}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-500' : ''}`} />
                {!isCollapsed && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom Actions: Logout & Collapse */}
        <div className="pt-2 border-t border-slate-200/80 dark:border-slate-800/80 space-y-1">
          {/* Sign Out Button */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-2 px-3 py-2 text-rose-500 hover:bg-rose-500/10 rounded-xl text-xs font-bold transition-colors ${
              isCollapsed ? 'justify-center px-0' : ''
            }`}
            title={isBn ? 'লগআউট' : 'Sign Out'}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!isCollapsed && <span>{isBn ? 'লগআউট' : 'Sign Out'}</span>}
          </button>

          {/* Collapse Toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full flex items-center justify-center p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-xs transition-colors"
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <div className="flex items-center gap-1.5">
                <ChevronLeft className="w-4 h-4" />
                <span className="text-[11px] font-bold">Collapse</span>
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Quick Add Modal */}
      <QuickAddModal isOpen={isQuickAddOpen} onClose={() => setIsQuickAddOpen(false)} />
    </>
  );
};
